import type { ChartArtifactPayload } from '@cotera/api';
import { CHART_PALETTE_SIZE, getChartPaletteColor } from './chart-palette';

/** Normalize labels for stable cross-chart color matching. */
export function normalizeChartColorLabel(label: string): string {
  return label.trim().toLowerCase();
}

/** Deterministic palette slot from a data label (series name or pie slice). */
export function chartLabelToPaletteIndex(label: string): number {
  const normalized = normalizeChartColorLabel(label);
  if (normalized === '') {
    return 0;
  }
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash * 33) ^ normalized.charCodeAt(i);
  }
  return Math.abs(hash) % CHART_PALETTE_SIZE;
}

export type ChartColorResolver = (label: string) => string;

/**
 * Build a color resolver for a single chart that guarantees distinct colors for
 * distinct labels — up to the palette size — while preserving cross-chart
 * stability where it can.
 *
 * Each label prefers its hashed slot (so "Revenue" stays the same color across
 * charts), but if two labels in *this* chart hash to the same slot, the later
 * one linear-probes to the next free slot instead of silently sharing a color.
 * The plain hash alone collides often (8 labels into 8 slots ≈ a coin-flip that
 * at least two match), which is why charts with a handful of series used to show
 * duplicate colors even with slots to spare.
 *
 * Once the base palette is full, extra labels get sequential overflow indices
 * (>= `CHART_PALETTE_SIZE`), which `getChartPaletteColor` renders as
 * lightness-shifted variants of the same hues — so a 9th/10th series stays
 * distinct instead of repeating a color.
 */
export function buildChartColorResolver(labels: string[]): ChartColorResolver {
  const slotByLabel = new Map<string, number>();
  const usedSlots = new Set<number>();
  let nextOverflowSlot = CHART_PALETTE_SIZE;

  for (const label of labels) {
    const key = normalizeChartColorLabel(label);
    if (slotByLabel.has(key)) {
      continue;
    }
    let slot: number;
    if (usedSlots.size < CHART_PALETTE_SIZE) {
      // Base ring: prefer the hashed slot, probe to the next free one on a clash.
      slot = chartLabelToPaletteIndex(label);
      let probes = 0;
      while (usedSlots.has(slot) && probes < CHART_PALETTE_SIZE) {
        slot = (slot + 1) % CHART_PALETTE_SIZE;
        probes += 1;
      }
      usedSlots.add(slot);
    } else {
      // Base ring full: hand out a lightness-shifted variant of the same hues.
      slot = nextOverflowSlot;
      nextOverflowSlot += 1;
    }
    slotByLabel.set(key, slot);
  }

  return (label: string): string => {
    const key = normalizeChartColorLabel(label);
    const slot = slotByLabel.get(key) ?? chartLabelToPaletteIndex(label);
    return getChartPaletteColor(slot);
  };
}

/** Labels that receive distinct colors within a chart payload. */
export function collectChartColorLabels(
  payload: ChartArtifactPayload
): string[] {
  if (payload.chart_type === 'pie') {
    return payload.data.labels;
  }
  return payload.data.datasets.map((dataset, index) => {
    const raw = dataset.label;
    if (raw !== null && raw.trim() !== '') {
      return raw;
    }
    return `Series ${index + 1}`;
  });
}

/** Primary accent label for single-hue spark previews (first series or chart title). */
export function chartPayloadAccentLabel(payload: ChartArtifactPayload): string {
  const firstDataset = payload.data.datasets[0];
  if (firstDataset !== undefined) {
    const raw = firstDataset.label;
    if (raw !== null && raw.trim() !== '') {
      return raw;
    }
  }
  if (payload.title.trim() !== '') {
    return payload.title;
  }
  return 'Series 1';
}
